import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getProjectTier } from '../config/pricing';

const getDifficultyColor = (difficulty) => {
  switch (difficulty) {
    case 'beginner':
      return { bg: '#dcfce7', text: '#166534' };
    case 'intermediate':
      return { bg: '#fef3c7', text: '#92400e' };
    case 'advanced':
      return { bg: '#fee2e2', text: '#991b1b' };
    default:
      return { bg: '#f3f4f6', text: '#374151' };
  }
};

// Platform badge colors
const getPlatformBadge = (projectId) => {
  switch (projectId) {
    case 'devsecops-github-aws':
      return { icon: '🐙', label: 'GitHub', bg: '#1f2937', border: '#6e7681' };
    case 'devsecops-gitlab-azure':
      return { icon: '🦊', label: 'GitLab', bg: '#292961', border: '#fc6d26' };
    case 'threat-modelling':
      return { icon: '🎯', label: 'Framework', bg: '#1e1b4b', border: '#818cf8' };
    case 'kubernetes-security':
      return { icon: '☸️', label: 'Kubernetes', bg: '#0c1929', border: '#326ce5' };
    case 'snowflake-security':
      return { icon: '❄️', label: 'Snowflake', bg: '#29b5e8', border: '#0ea5e9' };
    case 'databricks-security':
      return { icon: '🧱', label: 'Databricks', bg: '#ff3621', border: '#f59e0b' };
    case 'sagemaker-ml-security':
      return { icon: '🤖', label: 'SageMaker', bg: '#ff9900', border: '#f43f5e' };
    case 'colab-llm-security':
      return { icon: '📓', label: 'Google Colab', bg: '#f9ab00', border: '#84cc16' };
    case 'n8n-agent-security':
      return { icon: '🔗', label: 'n8n', bg: '#ea4b71', border: '#6366f1' };
    case 'ai-agent-incident-response':
      return { icon: '🚨', label: 'Incident Response', bg: '#dc2626', border: '#ef4444' };
    default:
      return null;
  }
};

// CI/CD badge
const getCICDBadge = (projectId) => {
  switch (projectId) {
    case 'devsecops-github-aws':
      return { icon: '⚡', label: 'GitHub Actions', color: '#2088ff' };
    case 'devsecops-gitlab-azure':
      return { icon: '🔄', label: 'GitLab CI', color: '#fc6d26' };
    case 'threat-modelling':
      return { icon: '🔍', label: 'STRIDE/DREAD', color: '#818cf8' };
    case 'kubernetes-security':
      return { icon: '🛡️', label: 'Trivy/Falco', color: '#00b4d8' };
    case 'snowflake-security':
      return { icon: '🔒', label: 'Data Security', color: '#0ea5e9' };
    case 'databricks-security':
      return { icon: '📊', label: 'Unity Catalog', color: '#f59e0b' };
    case 'sagemaker-ml-security':
      return { icon: '🧪', label: 'MLOps', color: '#f43f5e' };
    case 'colab-llm-security':
      return { icon: '🛡️', label: 'OWASP LLM', color: '#84cc16' };
    case 'n8n-agent-security':
      return { icon: '🤖', label: 'AI Agents', color: '#6366f1' };
    case 'ai-agent-incident-response':
      return { icon: '🔥', label: 'AI Breach', color: '#dc2626' };
    default:
      return null;
  }
};

// Cloud badge
const getCloudBadge = (projectId) => {
  switch (projectId) {
    case 'devsecops-github-aws':
      return { icon: '☁️', label: 'AWS', bg: '#ff9900', text: '#000' };
    case 'devsecops-gitlab-azure':
      return { icon: '☁️', label: 'Azure', bg: '#0078d4', text: '#fff' };
    case 'threat-modelling':
      return { icon: '🏛️', label: 'Enterprise', bg: '#6b21a8', text: '#fff' };
    case 'kubernetes-security':
      return { icon: '☁️', label: 'GCP', bg: '#4285f4', text: '#fff' };
    case 'snowflake-security':
      return { icon: '☁️', label: 'Multi-Cloud', bg: '#29b5e8', text: '#fff' };
    case 'databricks-security':
      return { icon: '☁️', label: 'Multi-Cloud', bg: '#ff3621', text: '#fff' };
    case 'sagemaker-ml-security':
      return { icon: '☁️', label: 'AWS', bg: '#ff9900', text: '#000' };
    case 'colab-llm-security':
      return { icon: '☁️', label: 'GCP', bg: '#4285f4', text: '#fff' };
    case 'n8n-agent-security':
      return { icon: '🏠', label: 'Self-Hosted', bg: '#6366f1', text: '#fff' };
    case 'ai-agent-incident-response':
      return { icon: '🏢', label: 'Enterprise', bg: '#dc2626', text: '#fff' };
    default:
      return null;
  }
};

const ProjectCard = ({ project, isPro, onSelect, progress }) => {
  const projectTier = getProjectTier(project.id);
  const isLocked = !isPro && projectTier !== 'free';
  const difficultyColors = getDifficultyColor(project.difficulty);
  const platformBadge = getPlatformBadge(project.id);
  const cicdBadge = getCICDBadge(project.id);
  const cloudBadge = getCloudBadge(project.id);

  // Calculate completion percentage
  const totalSteps = project.steps?.length || 0;
  const completedSteps = progress?.completedSteps?.length || 0;
  const completionPercent = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  // Calculate total tasks
  const totalTasks = project.steps?.reduce((acc, step) => acc + (step.content?.tasks?.length || 0), 0) || 0;

  return (
    <div
      onClick={() => !isLocked && onSelect(project)}
      style={{
        backgroundColor: '#1f2937',
        borderRadius: '16px',
        padding: '0',
        cursor: isLocked ? 'not-allowed' : 'pointer',
        opacity: isLocked ? 0.85 : 1,
        border: '1px solid #374151',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        if (!isLocked) {
          e.currentTarget.style.borderColor = '#6366f1';
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 24px rgba(99, 102, 241, 0.15)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#374151';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Top badges row - Platform, CI/CD, Cloud */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          padding: '16px 20px 0',
          flexWrap: 'wrap',
        }}
      >
        {/* Platform Badge */}
        {platformBadge && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: platformBadge.bg,
              border: `1px solid ${platformBadge.border}`,
              color: '#e5e7eb',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
            }}
          >
            <span>{platformBadge.icon}</span>
            {platformBadge.label}
          </span>
        )}

        {/* CI/CD Badge */}
        {cicdBadge && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: 'transparent',
              border: `1px solid ${cicdBadge.color}`,
              color: cicdBadge.color,
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
            }}
          >
            <span>{cicdBadge.icon}</span>
            {cicdBadge.label}
          </span>
        )}

        {/* Cloud Badge */}
        {cloudBadge && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: cloudBadge.bg,
              color: cloudBadge.text,
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
            }}
          >
            <span>{cloudBadge.icon}</span>
            {cloudBadge.label}
          </span>
        )}

        {/* Lock/Progress badge - top right */}
        {isLocked && (
          <span
            style={{
              marginLeft: 'auto',
              backgroundColor: projectTier === 'pro' ? '#6366f1' : '#10b981',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Free account
          </span>
        )}
        {!isLocked && completionPercent > 0 && (
          <span
            style={{
              marginLeft: 'auto',
              backgroundColor: completionPercent === 100 ? '#10b981' : '#6366f1',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
            }}
          >
            {completionPercent === 100 ? '✓ Complete' : `${completionPercent}%`}
          </span>
        )}
      </div>

      {/* Main content */}
      <div style={{ padding: '16px 20px' }}>
        {/* Title */}
        <h3
          style={{
            color: 'white',
            fontSize: '18px',
            fontWeight: '700',
            marginBottom: '8px',
            lineHeight: '1.3',
          }}
        >
          {project.icon && <span style={{ marginRight: '8px' }}>{project.icon}</span>}
          {project.title}
        </h3>

        {/* Subtitle */}
        <p
          style={{
            color: '#9ca3af',
            fontSize: '14px',
            marginBottom: '16px',
            lineHeight: '1.5',
          }}
        >
          {project.subtitle}
        </p>

        {/* Stats row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '16px',
            padding: '12px 14px',
            backgroundColor: '#111827',
            borderRadius: '10px',
          }}
        >
          {/* Difficulty */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                backgroundColor: difficultyColors.bg,
                color: difficultyColors.text,
                padding: '3px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                textTransform: 'uppercase',
              }}
            >
              {project.difficulty}
            </span>
          </div>

          {/* Duration */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9ca3af', fontSize: '13px' }}>
            <span>⏱️</span>
            <span>{project.estimatedHours}h</span>
          </div>

          {/* Steps */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9ca3af', fontSize: '13px' }}>
            <span>📋</span>
            <span>{totalSteps} steps</span>
          </div>

          {/* Tasks */}
          {totalTasks > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9ca3af', fontSize: '13px' }}>
              <span>✅</span>
              <span>{totalTasks} tasks</span>
            </div>
          )}
        </div>

        {/* Skills tags */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
          }}
        >
          {project.skills?.slice(0, 4).map((skill, index) => (
            <span
              key={index}
              style={{
                backgroundColor: '#374151',
                color: '#d1d5db',
                padding: '5px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
              }}
            >
              {skill}
            </span>
          ))}
          {project.skills?.length > 4 && (
            <span
              style={{
                backgroundColor: 'transparent',
                color: '#6366f1',
                padding: '5px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
              }}
            >
              +{project.skills.length - 4} more
            </span>
          )}
        </div>
      </div>

      {/* Progress bar for Pro users */}
      {!isLocked && completionPercent > 0 && completionPercent < 100 && (
        <div
          style={{
            padding: '0 20px 16px',
          }}
        >
          <div
            style={{
              backgroundColor: '#374151',
              borderRadius: '4px',
              height: '6px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${completionPercent}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* CTA for locked cards */}
      {isLocked && (
        <div
          style={{
            padding: '0 20px 20px',
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = '/portal?tab=projects';
            }}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.02)';
              e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span>🔓</span>
            Sign in to open
          </button>
        </div>
      )}

      {/* Start button for unlocked cards */}
      {!isLocked && completionPercent === 0 && (
        <div
          style={{
            padding: '0 20px 20px',
          }}
        >
          <button
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <span>🚀</span>
            Start Project
          </button>
        </div>
      )}

      {/* Continue button for in-progress cards */}
      {!isLocked && completionPercent > 0 && completionPercent < 100 && (
        <div
          style={{
            padding: '0 20px 20px',
          }}
        >
          <button
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <span>▶️</span>
            Continue Learning
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectCard;
